import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';

const PLAN_STATE = {
  paid: { label: 'Active', bg: '#dcfce7', color: '#15803d' },
  cancelled_in_period: { label: 'Cancelled, still running', bg: '#fef9c3', color: '#a16207' },
  trial: { label: 'Trial', bg: '#e0f2fe', color: '#0369a1' },
  expired: { label: 'Ended', bg: '#f1f5f9', color: '#64748b' },
  none: { label: 'App on, no plan', bg: '#f1f5f9', color: '#64748b' },
};

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

/** A student's mobile plan state, from their latest subscription (StudentOverviewService). */
export function MobilePlanChip({ plan }) {
  if (!plan) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  const state = PLAN_STATE[plan.state] || PLAN_STATE.expired;
  const until = formatDate(plan.period_end);
  const tip = [plan.plan_name, plan.provider && `via ${plan.provider}`, until && (plan.state === 'expired' ? `ended ${until}` : `until ${until}`)]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip title={tip || ''} arrow>
      <Box>
        <Chip size="small" label={state.label} sx={{ fontWeight: 700, bgcolor: state.bg, color: state.color }} />
        {plan.plan_name && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
            {plan.plan_name}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

const KINDS = [
  { key: 'bought', label: 'Bought' },
  { key: 'mobile_plan', label: 'Mobile plan' },
  { key: 'course', label: 'Course' },
  { key: 'other_free', label: 'Free (preview, pack)' },
];

/**
 * Distinct mock exams or past papers a student has, and how they got them. Only "Bought" is
 * money; the rest are free grants.
 */
export function AccessBreakdown({ data, inline = false }) {
  const total = data?.total ?? 0;
  if (!total) {
    return <Typography variant="body2" color="text.secondary">0</Typography>;
  }
  const parts = KINDS.filter((k) => (data?.[k.key] ?? 0) > 0);

  if (inline) {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {parts.map((k) => (
          <Chip key={k.key} size="small" variant={k.key === 'bought' ? 'filled' : 'outlined'} label={`${k.label}: ${data[k.key]}`} />
        ))}
      </Box>
    );
  }

  return (
    <Tooltip title={parts.map((k) => `${k.label}: ${data[k.key]}`).join(' · ')} arrow>
      <Box>
        <Typography variant="body2" fontWeight={700} color="#2d3748">{total}</Typography>
        <Typography variant="caption" color="text.secondary">
          {data.bought ? `${data.bought} bought` : 'none bought'}
        </Typography>
      </Box>
    </Tooltip>
  );
}
